import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { KnowledgeEntry } from "@/components/knowledge/types";
import React from 'react';

export function useKnowledgeEntries() {
  const queryClient = useQueryClient();

  const fetchKnowledgeEntries = async (): Promise<KnowledgeEntry[]> => {
    console.log("useKnowledgeEntries: Fetching entries from database");
    
    const { data, error } = await supabase
      .from('knowledge_entries')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error("useKnowledgeEntries: Database error:", error);
      throw error;
    }

    console.log("useKnowledgeEntries: Received entries:", data?.length || 0);

    return data?.map(entry => {
      // Add detailed logging of the raw metadata
      console.log("Raw metadata from database:", {
        entryId: entry.id,
        metadataType: typeof entry.metadata,
        rawMetadata: entry.metadata
      });

      const metadata = typeof entry.metadata === 'string' 
        ? JSON.parse(entry.metadata) 
        : (entry.metadata as Record<string, unknown>) || {};

      // Add detailed logging of the processed metadata
      console.log("Processed metadata:", {
        entryId: entry.id,
        hasTitle: !!metadata.generated_title,
        hasTags: !!metadata.generated_tags,
        hasSummary: !!metadata.summary,
        fullMetadata: metadata
      });

      // Ensure embedding is properly typed as number[] or null
      let processedEmbedding: number[] | null = null;
      if (typeof entry.embedding === 'string') {
        try {
          // Attempt to parse if it's a string representation of an array 
          const parsedEmbedding = JSON.parse(entry.embedding);
          if (Array.isArray(parsedEmbedding)) {
            processedEmbedding = parsedEmbedding;
          }
        } catch (e) {
          // If parsing fails, leave as null
          console.warn('Failed to parse embedding', e);
        }
      } else if (Array.isArray(entry.embedding)) {
        processedEmbedding = entry.embedding;
      }

      return {
        ...entry,
        embedding: processedEmbedding,
        embedding_status: (entry.embedding_status as 'pending' | 'completed' | 'failed') || 'pending',
        metadata: {
          ...metadata,
          pdf_type: metadata.pdf_type as 'text-pdf' | 'image-pdf' | 'mixed-pdf' | undefined,
          processing_timestamp: metadata.processing_timestamp as string | undefined,
          text_extraction_error: metadata.text_extraction_error as string | undefined,
          image_extraction_error: metadata.image_extraction_error as string | undefined,
          service_used: metadata.service_used as string | undefined,
          content_type: metadata.content_type as string | undefined,
          textract_processing_status: metadata.textract_processing_status as string | undefined,
          s3_location: metadata.s3_location as { bucket: string; key: string } | undefined,
          content_length: metadata.content_length as number | undefined
        }
      };
    }) || [];
  };

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['knowledgeEntries'],
    queryFn: fetchKnowledgeEntries,
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: true,
  });

  // Add logging for when entries change
  React.useEffect(() => {
    console.log("useKnowledgeEntries: Entries updated", {
      count: entries.length,
      hasMetadata: entries.some(entry => entry.metadata),
      sampleEntry: entries[0] ? {
        id: entries[0].id,
        hasMetadata: !!entries[0].metadata,
        metadataKeys: entries[0].metadata ? Object.keys(entries[0].metadata) : []
      } : null
    });
  }, [entries]);

  // Add logging for query invalidation
  const invalidateEntries = React.useCallback(async () => {
    console.log("Invalidating knowledge entries query");
    await queryClient.invalidateQueries({ queryKey: ['knowledgeEntries'] });
    // Add a small delay to ensure the database has time to update
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Force a refetch of the knowledge entries
    await queryClient.refetchQueries({ queryKey: ['knowledgeEntries'] });
    console.log("Knowledge entries query invalidated and refetched");
  }, [queryClient]);

  return {
    entries,
    isLoading,
    invalidateEntries
  };
}
